/** @format */

import React, { useState } from 'react';
import {
  Grid,
  IconButton,
  Button,
  Typography,
  Card,
  CardContent,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import createToast from 'components/Toast/Toast';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmDialog, SimpleDialog, InputV2, SelectV, ButtonLoading, Icons } from 'components';
import { useStyles } from './Style';
import { getDynamicObjectAction } from 'utility';
import GrantsServices from '../services/GrantsServices';

const DEFAULT_GRANT_DATA_TYPE = 'username';

export default function GrantItem({
  data,
  refreshList,
  popKey = () => { },
  itemKey,
  setAccountDetails,
  accDetails,
  ...props
}) {
  // --STATES--//
  const [isLoading, setIsLoading] = useState(false);

  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isNewPermissionOpen, setIsNewPermissionOpen] = useState(false);

  const [isDeleteGrantOpen, setIsDeleteGrantOpen] = useState(false);
  const [isDeleteUserGrantOpen, setIsDeleteUserGrantOpen] = useState(false);
  const [userGrantToDelete, setUserGrantToDelete] = useState(null);

  const [grantUserDataType, setGrantUserDataType] = useState(DEFAULT_GRANT_DATA_TYPE);
  const [, setGrantUserData] = useState('');

  // --HOOKS--//
  const actionsList =
    getDynamicObjectAction([
      'newAccessGrantedBusinessBtn',
      'deleteAccessGrantedBusinessBtn',
      'usersAccessGrantedBusinessBtn',
    ]);
  const classes = useStyles();
  const methods = useForm();
  const { handleSubmit, /* reset, */ control, register } = methods;

  // --API--//
  const onSubmit = async (user) => {
    try {
      const body = {
        identityType: user?.grantType,
        client_id: data?.clientId,
      };
      if (!user?.personData || user?.personData.trim() == '') {
        return;
      }
      setIsLoading(true);
      await GrantsServices.postUserGrant(user?.personData.trim(), body);

      createToast('موفق', 'درخواست دسترسی ایجاد شد و پس از تایید شما فعال خواهد شد.', 'success');

      setIsNewPermissionOpen(false);
      setIsLoading(false);
      refreshList();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const deleteGrant = async () => {
    try {
      setIsLoading(true);
      await GrantsServices.deleteGrant(data?.clientId);
      createToast('موفق', 'دسترسی با موفقیت حذف شد.', 'success');

      setIsDeleteGrantOpen(false);
      setIsLoading(false);

      refreshList();
    } catch (err) {
      setIsLoading(false);
    }
  };

  const deleteUserGrant = async () => {
    if (!userGrantToDelete) return;
    try {
      setIsLoading(true);
      const params = {
        client_id: data?.clientId,
        identityType: 'id',
      };
      await GrantsServices.deleteUserGrant(userGrantToDelete?.id, params);
      createToast('موفق', 'دسترسی کاربر با موفقیت حذف شد.', 'success');

      setIsPermissionsOpen(false);
      setIsDeleteUserGrantOpen(false);
      setUserGrantToDelete(null);
      setIsLoading(false);

      refreshList();
    } catch (err) {
      setIsLoading(false);
    }
  };

  // -- LOCAL VARIABLES --//

  let grantTypeOptions = [
    { value: 'username', label: 'نام کاربری' },
    { value: 'email', label: 'پست الکترونیک' },
    { value: 'phone_number', label: 'تلفن همراه' },
    { value: 'nationalcode', label: 'کد ملی' },
  ];

  // -- OTHER FUNCTIONS --//
  const handleCloseNewPermission = () => {
    setIsNewPermissionOpen(false);
    setGrantUserData('');
    setGrantUserDataType(DEFAULT_GRANT_DATA_TYPE);
  };

  return (
    <Card xs={12} className={classes.root}>
      <CardContent>
        {/* Titles */}
        <Grid container justifyContent="space-between" alignItems="flex-start">
          <Grid item className={classes.titlePart} xs={12} md={8}>
            <Typography variant="h5" className={classes.titleText}>
              {data?.clientName}
            </Typography>
            <Typography variant="h6" className={classes.subtitleText}>
              {data?.clientDescription}
            </Typography>
          </Grid>
          <Grid item className={classes.titleActions} xs={12} md={4}>
            <Grid container className={classes.titleActionsInside} justifyContent="flex-end">
              {data?.permittedUsers.length > 0 ? (
                actionsList['usersAccessGrantedBusinessBtn'] && (
                  <Button
                    onClick={() => setIsPermissionsOpen(true)}
                    startIcon={<Icons icon="UserSuccess" color='#2B3990' size='20' />}
                    className={classes.people}
                  >
                    {data?.permittedUsers.length} نفر دسترسی جایگزین
                    {data?.permittedUsers.length > 1 ? ' دارند' : ' دارد'}
                  </Button>
                )
              ) : null}
              {actionsList['newAccessGrantedBusinessBtn'] && (
                <Button onClick={() => setIsNewPermissionOpen(true)} className={classes.newGrant}>
                  اعطای دسترسی جدید
                </Button>
              )}
              {actionsList['deleteAccessGrantedBusinessBtn'] && (
                <IconButton
                  className={classes.actionsIcon}
                  onClick={() => {
                    setIsDeleteGrantOpen(true);
                  }}
                  size="large">
                  <Icons icon="Trash" color='#2B3990' size='20' />
                </IconButton>
              )}

            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.scopes}>
          {data?.scopes?.map((v, k) => (
            <Typography key={k} className={classes.scopeItem}>
              {v}
            </Typography>
          ))}
        </Grid>
      </CardContent>

      {/* User Grants List */}
      <SimpleDialog
        title="مشاهده دسترسی‌های داده شده"
        open={isPermissionsOpen}
        icon={<Icons icon="UserSuccess" color='#2B3990' size='20' />}
        onClose={() => setIsPermissionsOpen(false)}
      >
        <TableContainer elevation={0} component={Paper} className={classes.table}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell className={classes.tableCell} style={{ width: '70%' }}>
                  نام کاربر
                </TableCell>
                <TableCell className={classes.tableCell}>شناسه دسترسی</TableCell>
                <TableCell className={classes.tableCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {/* List Data Map */}
              {data?.permittedUsers.map((v, k) => (
                <TableRow key={1}>
                  <TableCell className={classes.tableDataCell}>
                    {v?.given_name} {v?.family_name}
                  </TableCell>
                  <TableCell className={classes.tableDataCell}>{v?.preferred_username}</TableCell>
                  <TableCell align="right" className={classes.tableDataCell}>
                    <IconButton
                      className={classes.actionsIcon}
                      onClick={() => {
                        setUserGrantToDelete(v);
                        setIsDeleteUserGrantOpen(true);
                      }}
                      size="large">
                      <Icons icon="Trash" color='#2B3990' size='20' />
                    </IconButton>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SimpleDialog>

      {/* User Grants List */}
      <SimpleDialog
        title={`اعطای دسترسی جدید به ${data?.clientName}`}
        open={isNewPermissionOpen}
        icon={<Icons icon="UserSuccess" color='#2B3990' size='20' />}
        actions={
          <>
            <ButtonLoading
              name="ثبت دسترسی"
              loading={isLoading}
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
            />
            <Button variant="contained" onClick={handleCloseNewPermission}>
              انصراف
            </Button>
          </>
        }
        onClose={handleCloseNewPermission}
      >
        <Typography className={classes.newPermissionHint}>
          در صورت تمایل می‌توانید به یک کاربر دیگر اجازه دهید بجایتان وارد این پذیرنده شود‌.
        </Typography>
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className={classes.form}
            noValidate
            autoComplete="off"
          >
            <SelectV
              name="grantType"
              options={grantTypeOptions}
              label="نوع شناسایی"
              control={control}
              register={register}
              rules={['required']}
              onChangeFunc={(k, e) => {
                setGrantUserDataType(e?.target?.value);
              }}
              defaultValue={grantUserDataType}
              errors={{}}
              fullWidth
            />
            <InputV2
              control={control}
              register={register}
              errors={{}}
              rules={['required']}
              name="personData"
              label={`${grantTypeOptions.find((v) => v?.value == grantUserDataType)?.label
                } کاربر مورد نظر`}
              mainStyle={{ width: '100%', marginLeft: 0, marginRight: 0 }}
              fullWidth
              dir="ltr"
              placeholder=""
              defaultValue=""
              onChangeValue={(v) => {
                setGrantUserData(v);
              }}
            />
          </form>
        </FormProvider>
      </SimpleDialog>

      { }
      <ConfirmDialog
        open={isDeleteUserGrantOpen}
        onClose={() => setIsDeleteUserGrantOpen(false)}
        onConfirm={deleteUserGrant}
        loading={isLoading}
        title="تایید حذف دسترسی"
        content="آیا از حذف دسترسی کاربر زیر اطمینان دارید؟"
        confirmButtonText="حذف دسترسی"
        cancelButtonProps="انصراف"
      >
        <br />
        <Typography>
          نام و نام‌خانوادگی: {`${userGrantToDelete?.given_name} ${userGrantToDelete?.family_name}`}
        </Typography>

        <Typography>شناسه کاربری: {userGrantToDelete?.preferred_username}</Typography>
      </ConfirmDialog>

      {/* Confirm Delete a User Grant */}
      <ConfirmDialog
        open={isDeleteGrantOpen}
        onClose={() => setIsDeleteGrantOpen(false)}
        onConfirm={deleteGrant}
        title="تایید حذف دسترسی"
        content={`آیا از حذف دسترسی "${data?.clientName}" اطمینان دارید؟`}
        confirmButtonText="حذف دسترسی"
        cancelButtonProps="انصراف"
        loading={isLoading}
      />
    </Card>
  );
}